import { ComponentType } from 'react';
import type { IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import type { PartnerGroupRules } from 'src/graphql/entities/group-rules/group-rules.types';
import { UserSettingId } from 'src/graphql/entities/user-setting-value/user-setting-value.types';
import { SidePanelPropertyKey, SidePanelSectionKey } from './layout.types';

/** partner-scoped rules that gate a field — checked against the selected partner */
export type SidePanelAllowedRules = PartnerGroupRules[];

export interface SidePanelRowProps {
  propertyKey: SidePanelPropertyKey;
}

export interface SidePanelPropertyDescriptor {
  key: SidePanelPropertyKey;
  label: string;
  group?: { key: string; label: string };
  defaultVisible: boolean;
  /** absent for catalog-only fields rendered by their own component (site/client cards) */
  Row?: ComponentType<SidePanelRowProps>;
  isEmpty?: boolean;
  icon?: string;
  iconLibrary?: IconLibrary;
  allowedRules?: SidePanelAllowedRules;
  /**
   * Opt out of drag-reorder. Also prepends the key for existing saved layouts
   * via reconcileLayout `leadingKeys` (pinned fields can't be dragged into place).
   */
  reorderable?: boolean;
}

export interface SidePanelSectionData {
  key: SidePanelSectionKey;
  label: string;
  loading: boolean;
  properties: SidePanelPropertyDescriptor[];
  notice?: string;
  hidePanelLabel?: boolean;
  /**
   * Opt-in for card-rendered sections whose card applies the layout order
   * itself (via useSidePanelSectionOrder) — enables drag-reorder in the modal
   * even though no property has a Row.
   */
  reorderable?: boolean;
}

export interface SidePanelSurfaceSettingIds {
  profiles: UserSettingId;
  personalization: UserSettingId;
  groupProfile: UserSettingId;
}

export interface SidePanelSurfaceConfig {
  surfaceKey: string;
  settingIds: SidePanelSurfaceSettingIds;
  useSections: () => SidePanelSectionData[];
}

export const filterAllowedProperties = (
  sections: SidePanelSectionData[],
  selectedPartnerId: number | null,
  checkValidity: (rules: SidePanelAllowedRules) => boolean,
): SidePanelSectionData[] => {
  if (selectedPartnerId === null) {
    return sections;
  }

  return sections.map((section) => ({
    ...section,
    properties: section.properties.filter(
      (descriptor) => !descriptor.allowedRules || checkValidity(descriptor.allowedRules),
    ),
  }));
};
